import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createEmailTemplate,
  EmailTemplateDto,
  getEmailTemplate,
  updateEmailTemplate,
} from "../../services/emails.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";

const templateSchema = Yup.object().shape({
  name: Yup.string().required("Nombre requerido"),
  subjectTemplate: Yup.string().required("Asunto requerido"),
  bodyHtmlTemplate: Yup.string().required("Contenido requerido"),
});

const EmailTemplateFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const isEdit = !!id;
  const [loading, setLoading] = useState<boolean>(!!id);
  const [initialTemplate, setInitialTemplate] =
    useState<EmailTemplateDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientError, setRecipientError] = useState<string | null>(null);
  useAppSettings();

  useEffect(() => {
    const loadTemplate = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const tpl = await getEmailTemplate(Number(id));
        setInitialTemplate(tpl);
      } catch (error) {
        console.error("Error cargando plantilla", error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [id]);

  const formik = useFormik<EmailTemplateDto>({
    enableReinitialize: true,
    initialValues: initialTemplate
      ? {
          ...initialTemplate,
          defaultTo: initialTemplate.defaultTo || [],
          defaultCc: initialTemplate.defaultCc || [],
          defaultBcc: initialTemplate.defaultBcc || [],
        }
      : {
          code: "",
          name: "",
          description: "",
          subjectTemplate: "",
          bodyHtmlTemplate: "",
          defaultTo: [],
          defaultCc: [],
          defaultBcc: [],
          active: true,
        },
    validationSchema: templateSchema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const preparePayload = (data: typeof values) => {
          const { code, id, ...rest } = data;

          const payload: any = { ...rest };
          delete payload.createdAt;
          delete payload.updatedAt;

          return {
            ...payload,
            defaultTo: payload.defaultTo || [],
            defaultCc: payload.defaultCc || [],
            defaultBcc: payload.defaultBcc || [],
          };
        };

        if (isEdit && id) {
          const payload = preparePayload(values);
          await updateEmailTemplate(
            Number(id),
            payload as Partial<EmailTemplateDto>
          );
        } else {
          const payload = preparePayload(values);
          await createEmailTemplate(payload as EmailTemplateDto);
        }
        navigate("/emails/plantillas");
      } catch (error) {
        console.error("Error guardando plantilla", error);
      } finally {
        setSaving(false);
      }
    },
  });

  const handleCancel = () => {
    navigate("/emails/plantillas");
  };

  const allRecipients = React.useMemo(
    () => [...(formik.values.defaultTo || [])],
    [formik.values.defaultTo]
  );

  const handleAddRecipient = () => {
    const email = recipientEmail.trim();
    if (!email) {
      setRecipientError("Ingresa un correo");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setRecipientError("Correo inválido");
      return;
    }

    const current = formik.values.defaultTo || [];
    if (current.includes(email)) {
      setRecipientError("El correo ya existe en la lista");
      return;
    }
    formik.setFieldValue("defaultTo", [...current, email]);

    setRecipientEmail("");
    setRecipientError(null);
  };

  const handleRemoveRecipient = (email: string) => {
    formik.setFieldValue(
      "defaultTo",
      (formik.values.defaultTo || []).filter((e) => e !== email)
    );
  };

  return (
    <Layout loading={loading}>
      <div>
        <Title
          baseTitle="Correos"
          basePath="/emails/plantillas"
          activeTitle={
            isEdit ? "Editar plantilla de correo" : "Nueva plantilla de correo"
          }
          title={
            isEdit ? "Editar plantilla de correo" : "Nueva plantilla de correo"
          }
        />

        <form onSubmit={formik.handleSubmit} className=" border-0 m-4 rounded">
          {/* Información básica en una sola columna */}
          <div style={styles.basicSection}>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Información básica</h2>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Código</label>
                    <input
                      type="text"
                      disabled
                      style={{
                        ...styles.input,
                        ...styles.inputDisabled,
                      }}
                      value={
                        isEdit
                          ? formik.values.code || ""
                          : "Se generará automáticamente"
                      }
                      placeholder="Se generará automáticamente"
                    />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={styles.label}>Nombre *</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Solicitud de rechazo de documento"
                      {...formik.getFieldProps("name")}
                    />
                  </div>
                </div>

                <div>
                  <label style={styles.label}>Descripción</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="Descripción opcional de la plantilla"
                    {...formik.getFieldProps("description")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contenido y vista previa en dos columnas */}
          <div style={styles.contentGrid}>
            <div style={styles.leftColumn}>
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>Contenido del correo</h2>
                </div>
                <div style={styles.cardBody}>
                  <div>
                    <div style={styles.labelRow}>
                      <label style={styles.label}>Asunto *</label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="text"
                        style={styles.input}
                        placeholder="Correo para solicitar rechazo de documento"
                        {...formik.getFieldProps("subjectTemplate")}
                      />
                      <button
                        type="button"
                        style={styles.recipientsButton}
                        onClick={() => setShowRecipientsModal(true)}
                      >
                        Destinatarios
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={styles.label}>Cuerpo HTML *</label>
                    <textarea
                      style={styles.textarea}
                      placeholder="<html>..."
                      rows={25}
                      {...formik.getFieldProps("bodyHtmlTemplate")}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.rightColumn}>
              <div style={styles.previewCard}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>Vista previa</h2>
                </div>
                <div style={styles.previewBody}>
                  <div style={styles.previewSubject}>
                    <strong>Asunto:</strong>{" "}
                    {formik.values.subjectTemplate || "(Sin asunto)"}
                  </div>
                  <div style={styles.previewDivider} />
                  <iframe
                    style={styles.previewIframe}
                    srcDoc={
                      formik.values.bodyHtmlTemplate || "<p>Sin contenido</p>"
                    }
                    title="Vista previa del email"
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
              disabled={saving}
            >
              <i
                className="fa-solid fa-arrow-left"
                style={{ fontSize: "18px" }}
              ></i>
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: saving ? 0.6 : 1,
              }}
              disabled={saving}
            >
              <i className="fa-solid fa-save" style={{ fontSize: "18px" }}></i>
              {saving ? "Guardando..." : "Guardar Plantilla"}
            </button>
          </div>
        </form>
        <ModalContainer
          open={showRecipientsModal}
          closeFn={() => {
            setShowRecipientsModal(false);
            setRecipientEmail("");
            setRecipientError(null);
          }}
          title="Destinatarios por defecto"
        >
          <div className="mb-3">
            <label className="form-label">Correo</label>
            <input
              type="email"
              className="form-control"
              placeholder="correo@ejemplo.com"
              value={recipientEmail}
              onChange={(e) => {
                setRecipientEmail(e.target.value);
                setRecipientError(null);
              }}
            />
          </div>

          {recipientError && (
            <div className="text-danger small mb-2">{recipientError}</div>
          )}

          <button
            type="button"
            className="btn btn-secondary w-100 mb-3"
            onClick={handleAddRecipient}
          >
            Agregar destinatario
          </button>

          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Correo</th>
                  <th style={{ width: "10%" }}></th>
                </tr>
              </thead>
              <tbody>
                {allRecipients.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-body text-opacity-60">
                      Aún no hay destinatarios configurados.
                    </td>
                  </tr>
                )}
                {allRecipients.map((email) => (
                  <tr key={email}>
                    <td>{email}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleRemoveRecipient(email)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalContainer>
      </div>
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {},
  headerToolbar: {},
  backButton: {},
  iconWrapper: {},
  subtitle: {},
  saveButton: {},
  basicSection: {
    marginBottom: "8px",
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
    gap: "12px",
    marginBottom: "16px",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  rightColumn: {
    position: "sticky" as const,
    top: "24px",
    height: "fit-content",
  },
  card: {
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "10px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background: "linear-gradient(135deg, #f7fafc 0%, #ffffff 100%)",
  },
  cardTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#2d3748",
    margin: 0,
  },
  cardBody: {
    padding: "12px 16px 14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  row: {
    display: "flex",
    gap: "8px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
    marginBottom: "8px",
  },
  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  input: {
    width: "100%",
    padding: "8px 10px",
    fontSize: "13px",
    border: "2px solid #e2e8f0",
    borderRadius: "6px",
    outline: "none",
    transition: "all 0.3s ease",
    background: "#fff",
  },
  inputDisabled: {
    background: "#f7fafc",
    color: "#718096",
    cursor: "not-allowed",
    opacity: 0.7,
  },
  textarea: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    transition: "all 0.3s ease",
    background: "#fff",
    fontFamily: "monospace",
    resize: "vertical" as const,
  },
  hint: {
    fontSize: "12px",
    color: "#718096",
    marginTop: "6px",
    fontStyle: "italic",
  },
  error: {
    fontSize: "12px",
    color: "#f56565",
    marginTop: "6px",
    fontWeight: "500",
  },
  previewToggle: {},
  recipientsButton: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    color: "#4a5568",
    padding: "6px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  switchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: "4px",
  },
  switchLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
  },
  switchHint: {
    fontSize: "13px",
    color: "#718096",
    marginTop: "4px",
  },
  previewCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    position: "sticky" as const,
    top: "24px",
  },
  previewBody: {
    padding: "24px",
  },
  previewSubject: {
    fontSize: "14px",
    color: "#4a5568",
    marginBottom: "16px",
  },
  previewDivider: {
    height: "1px",
    background: "#e2e8f0",
    marginBottom: "16px",
  },
  previewIframe: {
    width: "100%",
    height: "600px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "16px",
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
  },
  cancelButton: {
    background: "#fff",
    color: "#718096",
    border: "2px solid #e2e8f0",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
  },
  submitButton: {
    background: "#50B7C0",
    color: "#fff",
    border: "none",
    padding: "8px 18px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
  },
};

export default EmailTemplateFormPage;
